<template>
  <div v-if="show" class="export-modal-overlay" @click.self="$emit('close')">
    <div class="export-modal">
      <div class="modal-header">
        <h2>Export Conditional Access</h2>
        <button class="modal-close" @click="$emit('close')">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="20" height="20">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <div class="modal-section">
          <div class="section-title">Scope</div>
          <div class="scope-tabs">
            <button class="scope-tab" :class="{ active: scope === 'all' }" @click="scope = 'all'">All Policies</button>
            <button class="scope-tab" :class="{ active: scope === 'selected' }" @click="scope = 'selected'">Selected</button>
            <button class="scope-tab" :class="{ active: scope === 'locations' }" @click="scope = 'locations'">Named Locations Only</button>
          </div>
        </div>

        <div v-if="scope === 'selected'" class="modal-section">
          <div class="section-header-row">
            <div class="section-title">Policies ({{ selectedPolicyIds.length }} of {{ policies.length }} selected)</div>
            <div class="section-actions">
              <button class="link-btn" @click="selectAllPolicies">Select All</button>
              <button class="link-btn" @click="deselectAllPolicies">Clear</button>
            </div>
          </div>
          <div class="filter-pills">
            <button class="filter-pill" :class="{ active: stateFilter === 'all' }" @click="stateFilter = 'all'">All ({{ policies.length }})</button>
            <button class="filter-pill" :class="{ active: stateFilter === 'enabled' }" @click="stateFilter = 'enabled'">Enabled ({{ enabledCount }})</button>
            <button class="filter-pill" :class="{ active: stateFilter === 'disabled' }" @click="stateFilter = 'disabled'">Disabled ({{ disabledCount }})</button>
            <button class="filter-pill" :class="{ active: stateFilter === 'reportOnly' }" @click="stateFilter = 'reportOnly'">Report-Only ({{ reportOnlyCount }})</button>
          </div>
          <div class="policy-list">
            <label v-for="p in filteredPolicies" :key="p.policyId" class="policy-check-row">
              <input type="checkbox" :value="p.policyId" v-model="selectedPolicyIds" />
              <span class="state-pill" :class="'pill-' + p.state">
                <span class="pill-dot"></span>
                {{ p.state === 'enabledForReportingButNotEnforced' ? 'Report-Only' : p.state }}
              </span>
              <span class="policy-check-name">{{ p.displayName }}</span>
            </label>
          </div>
        </div>

        <div v-if="scope === 'locations'" class="modal-section">
          <div class="section-header-row">
            <div class="section-title">Named Locations ({{ selectedLocationIds.length }} of {{ locations.length }} selected)</div>
            <div class="section-actions">
              <button class="link-btn" @click="selectAllLocations">Select All</button>
              <button class="link-btn" @click="deselectAllLocations">Clear</button>
            </div>
          </div>
          <div class="policy-list">
            <label v-for="l in locations" :key="l.locationId" class="policy-check-row">
              <input type="checkbox" :value="l.locationId" v-model="selectedLocationIds" />
              <span v-if="l.isTrusted" class="trust-badge-sm">Trusted</span>
              <span class="policy-check-name">{{ l.displayName }}</span>
              <span class="policy-check-type">{{ formatLocationType(l.locationType) }}</span>
            </label>
          </div>
        </div>

        <div class="modal-section">
          <div class="section-title">Format</div>
          <div class="format-options">
            <label class="format-option" :class="{ active: format === 'pdf' }">
              <input type="radio" value="pdf" v-model="format" />
              <div class="format-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="24" height="24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/>
                </svg>
              </div>
              <span class="format-label">PDF Document</span>
              <span class="format-desc">Branded landscape report</span>
            </label>
            <label class="format-option" :class="{ active: format === 'csv' }">
              <input type="radio" value="csv" v-model="format" />
              <div class="format-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="24" height="24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"/>
                </svg>
              </div>
              <span class="format-label">CSV Spreadsheet</span>
              <span class="format-desc">Flat table, one row per policy</span>
            </label>
            <label class="format-option" :class="{ active: format === 'json' }">
              <input type="radio" value="json" v-model="format" />
              <div class="format-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="24" height="24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25M6.75 17.25L1.5 12l5.25-5.25M14.25 3l-4.5 18"/>
                </svg>
              </div>
              <span class="format-label">JSON Data</span>
              <span class="format-desc">Machine-readable, resolved names</span>
            </label>
          </div>
        </div>

        <div v-if="format === 'pdf'" class="modal-section">
          <div class="section-title">PDF Sections</div>
          <div class="section-toggles">
            <label class="toggle-row">
              <input type="checkbox" v-model="sections.cover" />
              <span>Cover page</span>
            </label>
            <label class="toggle-row">
              <input type="checkbox" v-model="sections.summary" />
              <span>Summary table</span>
            </label>
            <label class="toggle-row">
              <input type="checkbox" v-model="sections.details" />
              <span>Policy details</span>
            </label>
            <label class="toggle-row">
              <input type="checkbox" v-model="sections.aiSummaries" />
              <span>AI summaries</span>
            </label>
            <label class="toggle-row" v-if="scope !== 'locations'">
              <input type="checkbox" v-model="sections.namedLocations" />
              <span>Named locations appendix</span>
            </label>
          </div>
        </div>

        <div v-if="scope === 'all' && format === 'pdf'" class="modal-section">
          <label class="toggle-row">
            <input type="checkbox" v-model="sections.namedLocations" />
            <span>Include named locations</span>
          </label>
        </div>
      </div>

      <div class="modal-footer">
        <button class="cancel-btn" @click="$emit('close')">Cancel</button>
        <button class="export-btn" @click="handleExport" :disabled="!canExport || exporting">
          {{ exporting ? 'Generating...' : 'Export' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExportModal',
  props: {
    show: { type: Boolean, default: false },
    policies: { type: Array, default: function() { return []; } },
    locations: { type: Array, default: function() { return []; } },
    exporting: { type: Boolean, default: false }
  },
  data: function() {
    return {
      scope: 'all',
      format: 'pdf',
      stateFilter: 'all',
      selectedPolicyIds: [],
      selectedLocationIds: [],
      sections: {
        cover: true,
        summary: true,
        details: true,
        aiSummaries: true,
        namedLocations: true
      }
    };
  },
  computed: {
    enabledCount: function() {
      return this.policies.filter(function(p) { return p.state === 'enabled'; }).length;
    },
    disabledCount: function() {
      return this.policies.filter(function(p) { return p.state === 'disabled'; }).length;
    },
    reportOnlyCount: function() {
      return this.policies.filter(function(p) { return p.state === 'enabledForReportingButNotEnforced'; }).length;
    },
    filteredPolicies: function() {
      var self = this;
      if (self.stateFilter === 'all') return self.policies;
      var stateMap = { enabled: 'enabled', disabled: 'disabled', reportOnly: 'enabledForReportingButNotEnforced' };
      var targetState = stateMap[self.stateFilter];
      return self.policies.filter(function(p) { return p.state === targetState; });
    },
    canExport: function() {
      if (this.scope === 'selected') return this.selectedPolicyIds.length > 0;
      if (this.scope === 'locations') return this.selectedLocationIds.length > 0;
      return true;
    }
  },
  watch: {
    show: function(val) {
      if (val) {
        this.selectedPolicyIds = [];
        this.selectedLocationIds = [];
        this.scope = 'all';
        this.format = 'pdf';
        this.stateFilter = 'all';
        this.sections = { cover: true, summary: true, details: true, aiSummaries: true, namedLocations: true };
      }
    },
    scope: function(val) {
      if (val === 'locations') {
        if (this.selectedLocationIds.length === 0) {
          this.selectAllLocations();
        }
      } else if (val === 'selected') {
        if (this.selectedPolicyIds.length === 0) {
          this.selectAllPolicies();
        }
      }
    }
  },
  methods: {
    formatLocationType: function(type) {
      if (type === 'ipNamedLocation') return 'IP Range';
      if (type === 'countryNamedLocation') return 'Country';
      return type || '--';
    },
    selectAllPolicies: function() {
      this.selectedPolicyIds = this.policies.map(function(p) { return p.policyId; });
    },
    deselectAllPolicies: function() {
      this.selectedPolicyIds = [];
    },
    selectAllLocations: function() {
      this.selectedLocationIds = this.locations.map(function(l) { return l.locationId; });
    },
    deselectAllLocations: function() {
      this.selectedLocationIds = [];
    },
    handleExport: function() {
      var opts = { format: this.format };

      if (this.scope === 'selected') {
        opts.policyIds = this.selectedPolicyIds;
        opts.includeLocations = false;
      } else if (this.scope === 'locations') {
        opts.locationIds = this.selectedLocationIds;
        opts.includeLocations = true;
        opts.sections = {
          cover: this.sections.cover,
          summary: false,
          details: false,
          aiSummaries: false,
          namedLocations: true
        };
      } else {
        opts.sections = {
          cover: this.sections.cover,
          summary: this.sections.summary,
          details: this.sections.details,
          aiSummaries: this.sections.aiSummaries,
          namedLocations: this.sections.namedLocations
        };
      }

      this.$emit('export', opts);
    }
  }
};
</script>

<style scoped>
.export-modal-overlay {
  position: fixed; top: 0; right: 0; bottom: 0; left: 0;
  background: rgba(12,74,110,0.4); z-index: 200;
  display: flex; justify-content: center; align-items: center;
  backdrop-filter: blur(2px);
}
.export-modal {
  width: 560px; max-width: 92vw; max-height: 85vh;
  background: #ffffff; border-radius: 12px;
  box-shadow: 0 20px 60px rgba(3,105,161,0.15);
  display: flex; flex-direction: column; overflow: hidden;
}
.modal-header {
  padding: 16px 20px; border-bottom: 1px solid #e2e8f0;
  display: flex; justify-content: space-between; align-items: center;
}
.modal-header h2 { margin: 0; font-size: 1.125rem; font-weight: 700; color: #0C4A6E; }
.modal-close {
  background: none; border: none; color: #94a3b8; cursor: pointer;
  padding: 6px; border-radius: 8px; display: flex;
}
.modal-close:hover { color: #475569; background: #f1f5f9; }
.modal-body { padding: 20px; overflow-y: auto; flex: 1; }
.modal-section { margin-bottom: 20px; }
.modal-section:last-child { margin-bottom: 0; }
.section-title {
  font-size: 0.75rem; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;
}
.section-header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.section-actions { display: flex; gap: 8px; }
.link-btn {
  background: none; border: none; color: #0369A1; cursor: pointer;
  font-size: 0.75rem; font-weight: 500; padding: 2px 4px;
}
.link-btn:hover { text-decoration: underline; }

.scope-tabs { display: flex; gap: 8px; }
.scope-tab {
  flex: 1; padding: 8px 12px; border: 1px solid #e2e8f0; border-radius: 8px;
  background: #f8fafc; color: #64748b; font-size: 0.8125rem; font-weight: 500;
  cursor: pointer; transition: all 0.15s; font-family: 'Inter', sans-serif;
}
.scope-tab.active { background: #13173F; color: #fff; border-color: #13173F; }
.scope-tab:hover:not(.active) { border-color: #94a3b8; background: #f1f5f9; }

.filter-pills { display: flex; gap: 6px; margin-bottom: 10px; flex-wrap: wrap; }
.filter-pill {
  padding: 4px 10px; border: 1px solid #e2e8f0; border-radius: 14px;
  background: #fff; color: #64748b; font-size: 0.6875rem; font-weight: 600;
  cursor: pointer; transition: all 0.15s; font-family: 'Inter', sans-serif;
}
.filter-pill.active { background: #0369A1; color: #fff; border-color: #0369A1; }

.policy-list {
  max-height: 200px; overflow-y: auto;
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 4px;
}
.policy-check-row {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 8px; border-bottom: 1px solid #f8fafc;
  cursor: pointer; font-size: 0.8125rem;
}
.policy-check-row:last-child { border-bottom: none; }
.policy-check-row:hover { background: #f8fafc; }
.policy-check-row input[type="checkbox"] { flex-shrink: 0; accent-color: #35B35E; }
.policy-check-name { flex: 1; color: #0C4A6E; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.policy-check-type { font-size: 0.6875rem; color: #94a3b8; flex-shrink: 0; }

.state-pill {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.5625rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.04em; padding: 2px 8px; border-radius: 10px; flex-shrink: 0;
}
.pill-dot { width: 5px; height: 5px; border-radius: 50%; }
.pill-enabled { background: #dcfce7; color: #047857; }
.pill-enabled .pill-dot { background: #047857; }
.pill-disabled { background: #f1f5f9; color: #64748b; }
.pill-disabled .pill-dot { background: #94a3b8; }
.pill-enabledForReportingButNotEnforced { background: #fef3c7; color: #92400e; }
.pill-enabledForReportingButNotEnforced .pill-dot { background: #92400e; }
.trust-badge-sm { background: #dcfce7; color: #047857; font-size: 0.5625rem; font-weight: 600; padding: 1px 6px; border-radius: 8px; flex-shrink: 0; }

.format-options { display: flex; gap: 10px; }
.format-option {
  flex: 1; border: 2px solid #e2e8f0; border-radius: 10px; padding: 14px;
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  cursor: pointer; transition: all 0.15s; text-align: center;
}
.format-option input[type="radio"] { display: none; }
.format-option.active { border-color: #13173F; background: #f8fafc; }
.format-option:hover:not(.active) { border-color: #94a3b8; }
.format-icon { color: #64748b; }
.format-option.active .format-icon { color: #13173F; }
.format-label { font-size: 0.8125rem; font-weight: 700; color: #0C4A6E; }
.format-desc { font-size: 0.625rem; color: #94a3b8; }

.section-toggles { display: flex; flex-direction: column; gap: 6px; }
.toggle-row {
  display: flex; align-items: center; gap: 8px;
  font-size: 0.8125rem; color: #334155; cursor: pointer;
}
.toggle-row input[type="checkbox"] { accent-color: #35B35E; }

.modal-footer {
  padding: 16px 20px; border-top: 1px solid #e2e8f0;
  display: flex; justify-content: flex-end; gap: 10px;
}
.cancel-btn {
  padding: 8px 16px; border: 1px solid #e2e8f0; border-radius: 8px;
  background: #fff; color: #64748b; font-size: 0.8125rem; font-weight: 500;
  cursor: pointer; font-family: 'Inter', sans-serif;
}
.cancel-btn:hover { background: #f8fafc; }
.export-btn {
  padding: 8px 20px; border: none; border-radius: 8px;
  background: #13173F; color: #fff; font-size: 0.8125rem; font-weight: 600;
  cursor: pointer; font-family: 'Inter', sans-serif; transition: background 0.15s;
}
.export-btn:hover:not(:disabled) { background: #1F243A; }
.export-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
